import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";

const REWARD_LABELS = {
  spotlight_placement: "Spotlight placement",
  promotional_reach: "Promotional reach",
  display_plays: "Display plays",
  position_plays: "Position plays bonus",
  coins: "Coins",
  profile_stats: "Profile stats",
  rankings: "Rankings",
  share_cards: "Share cards",
  activity_feed: "Activity feed",
  rgm_report: "RGM Report",
};

const POSITION_COIN_AMOUNTS = { 1: 500, 2: 250, 3: 100 };
const POSITION_PLAYS_AMOUNTS = { 1: 5000, 2: 2500, 3: 1000 };

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(d).slice(0, 10); }
};

const StatChip = ({ label, value, accent = "purple" }) => (
  <div className={`px-3 py-2 rounded-lg bg-${accent}-500/10 border border-${accent}-500/30`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
    <div className="text-lg font-bold text-white">{value}</div>
  </div>
);

const VerifiedBadge = ({ verified }) =>
  verified ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[11px] font-semibold" data-testid="verified-badge">
      ✅ Reward Applied
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[11px] font-semibold" data-testid="needs-repair-badge">
      ⚠ Needs Repair
    </span>
  );

const ChecksGrid = ({ checks }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mt-2">
    {Object.entries(checks || {}).map(([k, v]) => (
      <div
        key={k}
        className={`text-[11px] px-2 py-1 rounded border ${v ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-rose-500/10 border-rose-500/30 text-rose-300"}`}
        data-testid={`check-${k}-${v ? "ok" : "missing"}`}
      >
        {v ? "✓" : "✗"} {REWARD_LABELS[k] || k}
      </div>
    ))}
  </div>
);

const AdminSpotlightAuditPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Part A
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Coin backfill (Phase 15.6C)
  const [coinBackfilling, setCoinBackfilling] = useState(false);
  const [coinBackfillResult, setCoinBackfillResult] = useState(null);

  // Part B + D
  const [audit, setAudit] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Part C
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [repairHistory, setRepairHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!me.data || me.data.role !== "admin") {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        setUser(me.data);
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate]);

  const fetchAuditAndDiag = useCallback(async () => {
    setLoading(true);
    try {
      const [a, d, h] = await Promise.all([
        api.get("/admin/spotlight-audit/audit"),
        api.get("/admin/spotlight-audit/diagnostics"),
        api.get("/admin/spotlight-audit/repair-history", { params: { limit: 20 } }),
      ]);
      setAudit(a.data);
      setDiagnostics(d.data);
      setRepairHistory(h.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchAuditAndDiag(); }, [user, fetchAuditAndDiag]);

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await api.post("/admin/spotlight-audit/backfill-history-reports");
      setBackfillResult(r.data);
      toast.success(`Backfill complete — ${r.data.created} reports created, ${r.data.skipped} skipped (duplicates).`);
      await fetchAuditAndDiag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const handleCoinBackfill = async () => {
    if (!window.confirm("Grant historical Spotlight coin rewards + position-stacked promo plays to every winner? Idempotent — safe to re-run.")) return;
    setCoinBackfilling(true);
    setCoinBackfillResult(null);
    try {
      const r = await api.post("/admin/spotlight-audit/backfill-coins");
      setCoinBackfillResult(r.data);
      toast.success(`Coin backfill complete — +${(r.data.total_coins_granted || 0).toLocaleString()} coins · +${(r.data.total_position_plays_granted || 0).toLocaleString()} plays granted.`);
      await fetchAuditAndDiag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Coin backfill failed");
    } finally {
      setCoinBackfilling(false);
    }
  };

  const handleRepair = async (submissionId = null) => {
    if (!submissionId) {
      if (!window.confirm("Backfill missing Spotlight rewards for ALL winners? Idempotent — safe to re-run.")) return;
    }
    setRepairing(true);
    setRepairResult(null);
    try {
      const r = await api.post("/admin/spotlight-audit/repair", submissionId ? { submission_id: submissionId } : {});
      setRepairResult(r.data);
      toast.success(`Repair complete — ${r.data.changed} winners updated of ${r.data.total}.`);
      await fetchAuditAndDiag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-spotlight-audit-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-white">🏆 Spotlight Audit & History</h1>
          <p className="text-gray-400 text-sm mt-1">
            Audit every Spotlight Winner. Backfill missing rewards. Preserve every winner in RGM Reports.
          </p>
        </header>

        {/* Summary tiles */}
        {diagnostics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="audit-summary-tiles">
            <StatChip label="Total winners" value={diagnostics.total_winners} accent="purple" />
            <StatChip label="Fully rewarded" value={diagnostics.verified_count} accent="emerald" />
            <StatChip label="Needs repair" value={diagnostics.needs_repair_count} accent="rose" />
            <StatChip label="All OK" value={diagnostics.needs_repair_count === 0 ? "Yes" : "No"} accent="amber" />
            <StatChip label="🪙 Total coins granted" value={(diagnostics.total_coins_granted || 0).toLocaleString()} accent="amber" />
            <StatChip label="🪙 Coins owed" value={(diagnostics.total_coins_owed || 0).toLocaleString()} accent="rose" />
          </div>
        )}

        {/* Phase 15.6C — Coin Rewards Backfill */}
        <Card className="mb-6 p-4 bg-gradient-to-br from-amber-900/30 to-yellow-900/10 border-amber-500/40">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-white font-bold text-lg">🪙 Backfill Spotlight Coins</h2>
              <p className="text-gray-400 text-xs mt-0.5 max-w-2xl">
                Grant historical coin + position-stacked promo play rewards to every Spotlight Winner.
                <span className="block mt-1 text-amber-200">
                  🥇 #1 → +500 coins + 5,000 plays · 🥈 #2 → +250 coins + 2,500 plays · 🥉 #3 → +100 coins + 1,000 plays
                </span>
                Active accounts only. Idempotent — safe to re-run.
              </p>
            </div>
            <Button
              onClick={handleCoinBackfill}
              disabled={coinBackfilling}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold"
              data-testid="backfill-spotlight-coins-btn"
            >
              {coinBackfilling ? "Granting…" : "🪙 Backfill Spotlight Coins"}
            </Button>
          </div>
          {coinBackfillResult && (
            <div className="mt-4 p-3 bg-black/40 rounded border border-amber-500/30 text-sm" data-testid="coin-backfill-result">
              <div className="text-emerald-300 font-semibold mb-1">
                ✓ {coinBackfillResult.winners_processed} winners processed
              </div>
              <div className="text-xs text-amber-200 grid grid-cols-2 gap-2 mt-2">
                <div className="bg-gray-900/50 rounded px-3 py-2">
                  <div className="text-gray-400 text-[10px] uppercase">Coins granted (this run)</div>
                  <div className="text-amber-300 font-bold text-lg">+{(coinBackfillResult.total_coins_granted || 0).toLocaleString()} 🪙</div>
                </div>
                <div className="bg-gray-900/50 rounded px-3 py-2">
                  <div className="text-gray-400 text-[10px] uppercase">Position plays granted (this run)</div>
                  <div className="text-amber-300 font-bold text-lg">+{(coinBackfillResult.total_position_plays_granted || 0).toLocaleString()} ▶</div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Part A — Backfill RGM Reports history */}
        <Card className="mb-6 p-4 bg-gradient-to-br from-purple-900/30 to-fuchsia-900/10 border-purple-500/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-white font-bold text-lg">📰 RGM Reports History Backfill</h2>
              <p className="text-gray-400 text-xs mt-0.5 max-w-2xl">
                Publish historical achievement cards for every Verified Artist, Spotlight Winner,
                Founding Artist, Crew Seeder, Featured Producer, and the 100+ artist milestone.
                Idempotent.
              </p>
            </div>
            <Button
              onClick={handleBackfill}
              disabled={backfilling}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              data-testid="backfill-history-reports-btn"
            >
              {backfilling ? "Backfilling…" : "Backfill history reports"}
            </Button>
          </div>
          {backfillResult && (
            <div className="mt-4 p-3 bg-black/40 rounded border border-purple-500/30 text-sm" data-testid="backfill-result">
              <div className="text-emerald-300 font-semibold mb-1">
                ✓ {backfillResult.created} new reports · {backfillResult.skipped} skipped (already existed)
              </div>
              <div className="text-xs text-gray-400 grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {Object.entries(backfillResult.by_category || {}).map(([cat, c]) => (
                  <div key={cat} className="bg-gray-900/50 rounded px-2 py-1">
                    <div className="text-white font-semibold">{cat}</div>
                    <div>+{c.created} · skipped {c.skipped}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Part C — Repair all */}
        <Card className="mb-6 p-4 bg-gradient-to-br from-amber-900/30 to-rose-900/10 border-amber-500/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-white font-bold text-lg">🛠 Repair Missing Spotlight Rewards</h2>
              <p className="text-gray-400 text-xs mt-0.5 max-w-2xl">
                Sweeps every winner. For each missing reward (promo plays, boost, badge,
                engagement bump, status, notification, report) — backfill it. Idempotent;
                every action is logged in <span className="text-amber-300">spotlight_repair_audit</span>.
              </p>
            </div>
            <Button
              onClick={() => handleRepair(null)}
              disabled={repairing || (audit && audit.needs_repair === 0)}
              className="bg-gradient-to-r from-amber-600 to-rose-600 text-white"
              data-testid="repair-all-btn"
            >
              {repairing ? "Repairing…" : audit && audit.needs_repair === 0 ? "Nothing to repair" : "Repair all missing"}
            </Button>
          </div>
          {repairResult && (
            <div className="mt-4 p-3 bg-black/40 rounded border border-amber-500/30 text-sm" data-testid="repair-result">
              <div className="text-emerald-300 font-semibold">
                ✓ {repairResult.changed} of {repairResult.total} winners repaired
              </div>
            </div>
          )}
        </Card>

        {/* Part B + D — Winner audit table */}
        <h2 className="text-white font-bold text-lg mb-3">
          Winners ({audit ? audit.winners_audited : 0})
        </h2>
        {loading && <div className="text-gray-400 mb-3">Loading audit…</div>}
        {audit && audit.rows.length === 0 && (
          <Card className="p-6 text-center text-gray-400 bg-gray-900/40 border-gray-800">
            No Spotlight winners on record yet.
          </Card>
        )}
        <div className="space-y-2" data-testid="audit-rows">
          {audit && audit.rows.map((r) => (
            <Card
              key={`${r.spotlight_id}-${r.submission_id}-${r.position}`}
              className={`p-3 border ${r.fully_rewarded ? "bg-emerald-900/10 border-emerald-500/20" : "bg-rose-900/10 border-rose-500/30"}`}
              data-testid={`audit-row-${r.submission_id}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-2xl">{r.position === 1 ? "🥇" : r.position === 2 ? "🥈" : r.position === 3 ? "🥉" : "•"}</span>
                    <span className="text-white font-bold truncate">{r.artist_name || "Unknown"}</span>
                    <span className="text-gray-400">— {r.track_title || "untitled"}</span>
                    <Badge variant="outline" className="border-purple-500/40 text-purple-300 text-[10px]">
                      {r.week_label || "Spotlight"}
                    </Badge>
                    <VerifiedBadge verified={r.fully_rewarded} />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <span>📅 {formatDate(r.week_start)}</span>
                    <span>Tier: <span className="text-purple-300">{r.expected_tier || "—"}</span></span>
                    <span>Promo plays: <span className="text-amber-300">{r.promotional_plays.toLocaleString()}</span></span>
                    <span>Boost target: <span className="text-amber-300">{r.boost_target.toLocaleString()}</span></span>
                    <span>Status: <span className="text-gray-300">{r.status || "—"}</span></span>
                  </div>
                  {/* Phase 15.6C — Reward summary row */}
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`reward-chips-${r.submission_id}`}>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${r.coins_granted ? "bg-amber-500/20 border-amber-400/40 text-amber-200" : "bg-rose-500/15 border-rose-400/40 text-rose-300"}`}
                      data-testid={`chip-coins-${r.coins_granted ? "ok" : "missing"}-${r.submission_id}`}
                    >
                      {r.coins_granted ? "✅" : "⚠"} Coins +{(r.coins_amount || POSITION_COIN_AMOUNTS[r.position] || 0).toLocaleString()} 🪙
                    </span>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${r.position_plays_granted ? "bg-amber-500/20 border-amber-400/40 text-amber-200" : "bg-rose-500/15 border-rose-400/40 text-rose-300"}`}
                      data-testid={`chip-position-plays-${r.position_plays_granted ? "ok" : "missing"}-${r.submission_id}`}
                    >
                      {r.position_plays_granted ? "✅" : "⚠"} Plays +{(r.position_plays_amount || POSITION_PLAYS_AMOUNTS[r.position] || 0).toLocaleString()} ▶
                    </span>
                    {r.checks?.rgm_report && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-500/20 border-emerald-400/40 text-emerald-300 font-semibold">
                        ✅ Report
                      </span>
                    )}
                    {r.checks?.profile_stats && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-500/20 border-emerald-400/40 text-emerald-300 font-semibold">
                        ✅ Badge
                      </span>
                    )}
                    {r.checks?.activity_feed && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-500/20 border-emerald-400/40 text-emerald-300 font-semibold">
                        ✅ Notified
                      </span>
                    )}
                  </div>
                  <ChecksGrid checks={r.checks} />
                </div>
                {!r.fully_rewarded && (
                  <Button
                    size="sm"
                    onClick={() => handleRepair(r.submission_id)}
                    disabled={repairing}
                    className="bg-amber-600 text-white"
                    data-testid={`repair-one-${r.submission_id}`}
                  >
                    Repair this
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Repair history */}
        {repairHistory.length > 0 && (
          <div className="mt-8">
            <h2 className="text-white font-bold text-lg mb-3">Recent repairs</h2>
            <Card className="p-3 bg-gray-900/40 border-gray-800">
              <ul className="text-xs text-gray-300 space-y-2" data-testid="repair-history-list">
                {repairHistory.map((h) => (
                  <li key={h.audit_id} className="flex flex-wrap gap-x-3">
                    <span className="text-gray-500">{formatDate(h.created_at)}</span>
                    <span className="text-purple-300">{h.week_label}</span>
                    <span className="text-white">#{h.position}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-emerald-300">{(h.actions || []).length} action(s)</span>
                    <span className="text-rose-300">before: {(h.before_missing || []).join(", ") || "none"}</span>
                    <span className="text-emerald-300">after: {(h.after_missing || []).join(", ") || "all_ok"}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSpotlightAuditPage;
